/**
 * FlowWizard Component
 *
 * Multi-step wizard for creating flows.
 * Initial setup before visual builder.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Monitor,
  Zap,
  Clock,
  Database,
  Radio,
  ChevronRight,
  ChevronLeft,
  Check,
  Plus,
  Trash2,
  AlertCircle
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  StandardCheckbox,
  useToast
} from '../../standard';
import { flowAutomationStorage, FLOW_TYPE_DEFINITIONS } from '../../../services/flowAutomationStorage';
import * as flowAutomationStorageRouter from '../../../services/flowAutomationStorageRouter';
import type {
  FlowType,
  CreateFlowPayload,
  FlowVariableInput,
  VariableType,
  RecordTriggerType,
  ScheduleFrequency
} from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface FlowWizardProps {
  flowId?: string; // For editing existing flow
  onSave: (flowId: string) => void;
  onCancel: () => void;
}

interface WizardStep {
  id: number;
  name: string;
  description: string;
}

// ============================================
// CONSTANTS
// ============================================

const WIZARD_STEPS: WizardStep[] = [
  { id: 1, name: 'Flow Type', description: 'Select the type of flow' },
  { id: 2, name: 'Basic Info', description: 'Name and description' },
  { id: 3, name: 'Configuration', description: 'Trigger configuration' },
  { id: 4, name: 'Review', description: 'Review and create' }
];

const OBJECT_TYPES = [
  { value: 'Client', label: 'Client' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Task', label: 'Task' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Account', label: 'Account' },
  { value: 'CreditReport', label: 'Credit Report' },
  { value: 'CyberSweepClient', label: 'CyberSweep Client' }
];

const VARIABLE_TYPES: { value: VariableType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'picklist', label: 'Picklist' },
  { value: 'record', label: 'Record' },
  { value: 'collection', label: 'Collection' }
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' }
];

// ============================================
// FLOW TYPE CARD COMPONENT
// ============================================

interface FlowTypeCardProps {
  type: FlowType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  selected: boolean;
  onClick: () => void;
}

const FlowTypeCard: React.FC<FlowTypeCardProps> = ({
  label,
  description,
  icon,
  color,
  selected,
  onClick
}) => {
  const colorClasses: Record<string, string> = {
    blue: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    yellow: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    purple: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
    green: 'border-green-500 bg-green-50 dark:bg-green-900/20',
    orange: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
  };

  const iconBgClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
  };

  return (
    <button
      onClick={onClick}
      className={`p-6 rounded-xl border-2 text-left transition-all ${
        selected
          ? colorClasses[color]
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${iconBgClasses[color]}`}>
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {label}
            </h3>
            {selected && (
              <div className={`p-1 rounded-full ${iconBgClasses[color]}`}>
                <Check size={14} />
              </div>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
};

// ============================================
// VARIABLE INPUT COMPONENT
// ============================================

interface VariableInputProps {
  variable: FlowVariableInput;
  index: number;
  onChange: (index: number, field: keyof FlowVariableInput, value: unknown) => void;
  onRemove: (index: number) => void;
}

const VariableInput: React.FC<VariableInputProps> = ({
  variable,
  index,
  onChange,
  onRemove
}) => (
  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
    <div className="flex-1 grid grid-cols-2 gap-3">
      <StandardInput
        label="Variable Name"
        value={variable.name}
        onChange={(e) => onChange(index, 'name', e.target.value)}
        placeholder="e.g., inputAccountId"
        required
      />
      <StandardSelect
        label="Type"
        value={variable.type}
        onChange={(e) => onChange(index, 'type', e.target.value)}
        options={VARIABLE_TYPES}
      />
      {(variable.type === 'record' || variable.type === 'collection') && (
        <StandardSelect
          label="Object Type"
          value={variable.objectType || ''}
          onChange={(e) => onChange(index, 'objectType', e.target.value)}
          options={[{ value: '', label: 'Select Object' }, ...OBJECT_TYPES]}
        />
      )}
      <div className="flex items-center gap-4 col-span-2">
        <StandardCheckbox
          label="Available for Input"
          checked={variable.isInput || false}
          onChange={(checked) => onChange(index, 'isInput', checked)}
        />
        <StandardCheckbox
          label="Available for Output"
          checked={variable.isOutput || false}
          onChange={(checked) => onChange(index, 'isOutput', checked)}
        />
      </div>
    </div>
    <StandardButton
      variant="ghost"
      size="sm"
      onClick={() => onRemove(index)}
      className="text-red-500 hover:text-red-600"
    >
      <Trash2 size={16} />
    </StandardButton>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export const FlowWizard: React.FC<FlowWizardProps> = ({
  flowId,
  onSave,
  onCancel
}) => {
  const { addToast } = useToast();
  const isEditing = Boolean(flowId);

  // Step state
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [selectedType, setSelectedType] = useState<FlowType>('screen_flow');
  const [name, setName] = useState('');
  const [apiName, setApiName] = useState('');
  const [description, setDescription] = useState('');

  // Schedule config
  const [scheduleFrequency, setScheduleFrequency] = useState<ScheduleFrequency>('daily');
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const [scheduleStartTime, setScheduleStartTime] = useState('09:00');
  const [scheduleTimezone, setScheduleTimezone] = useState('America/New_York');

  // Record trigger config
  const [triggerObjectType, setTriggerObjectType] = useState('');
  const [triggerType, setTriggerType] = useState<RecordTriggerType>('create');
  const [runAsync, setRunAsync] = useState(true);

  // Variables
  const [variables, setVariables] = useState<FlowVariableInput[]>([]);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!flowId) return;
    let cancelled = false;
    flowAutomationStorageRouter.getFlowAsync(flowId).then(flow => {
      if (cancelled || !flow) return;
      setSelectedType(flow.type);
      setName(flow.name);
      setApiName(flow.apiName);
      setDescription(flow.description);
      if (flow.scheduleConfig) {
        setScheduleFrequency(flow.scheduleConfig.frequency);
        setScheduleStartDate(flow.scheduleConfig.startDate);
        setScheduleStartTime(flow.scheduleConfig.startTime);
        setScheduleTimezone(flow.scheduleConfig.timezone);
      }
      if (flow.recordTriggerConfig) {
        setTriggerObjectType(flow.recordTriggerConfig.objectType);
        setTriggerType(flow.recordTriggerConfig.triggerType);
        setRunAsync(flow.recordTriggerConfig.runAsync);
      }
      setVariables(flow.variables.map(v => ({
        name: v.name,
        apiName: v.apiName,
        type: v.type,
        objectType: v.objectType,
        isInput: v.isInput,
        isOutput: v.isOutput,
        description: v.description
      })));
    });
    return () => { cancelled = true; };
  }, [flowId]);

  // Auto-generate API name from name
  useEffect(() => {
    if (!flowId && name && !apiName) {
      const generated = name
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/^_+|_+$/g, '');
      setApiName(generated);
    }
  }, [name, apiName, flowId]);

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 2:
        if (!name.trim()) {
          newErrors.name = 'Flow name is required';
        }
        if (!apiName.trim()) {
          newErrors.apiName = 'API name is required';
        } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(apiName)) {
          newErrors.apiName = 'API name must start with a letter and contain only letters, numbers, and underscores';
        }
        break;

      case 3:
        if (selectedType === 'scheduled') {
          if (!scheduleStartDate) {
            newErrors.scheduleStartDate = 'Start date is required';
          }
        }
        if (selectedType === 'record_triggered') {
          if (!triggerObjectType) {
            newErrors.triggerObjectType = 'Object type is required';
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Variable management
  const addVariable = () => {
    setVariables([
      ...variables,
      {
        name: '',
        apiName: '',
        type: 'text',
        isInput: true,
        isOutput: false
      }
    ]);
  };

  const updateVariable = (index: number, field: keyof FlowVariableInput, value: unknown) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-generate API name
    if (field === 'name' && typeof value === 'string') {
      updated[index].apiName = value
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase();
    }

    setVariables(updated);
  };

  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    const payload: CreateFlowPayload = {
      name,
      apiName,
      description,
      type: selectedType,
      variables: variables.filter(v => v.name.trim())
    };

    // Add type-specific config
    if (selectedType === 'scheduled') {
      payload.scheduleConfig = {
        frequency: scheduleFrequency,
        startDate: scheduleStartDate,
        startTime: scheduleStartTime,
        timezone: scheduleTimezone
      };
    }

    if (selectedType === 'record_triggered') {
      payload.recordTriggerConfig = {
        objectType: triggerObjectType,
        triggerType: triggerType,
        runAsync: runAsync,
        optimizeForFastFieldUpdates: false,
        reevaluateCriteria: false,
        runOnlyIfRecordUpdatedToMeetCriteria: false
      };
    }

    try {
      let savedFlowId: string;
      if (flowId) {
        const updated = await flowAutomationStorageRouter.updateFlowAsync({
          id: flowId,
          name,
          description,
          variables: variables.filter(v => v.name.trim()),
          scheduleConfig: payload.scheduleConfig,
          recordTriggerConfig: payload.recordTriggerConfig
        });
        savedFlowId = updated ? flowId : flowId;
        addToast({ type: 'success', title: 'Flow Updated', message: `"${name}" has been updated` });
      } else {
        const newFlow = await flowAutomationStorageRouter.createFlowAsync(payload);
        savedFlowId = newFlow.id;
        addToast({ type: 'success', title: 'Flow Created', message: `"${name}" has been created as a draft` });
      }
      onSave(savedFlowId);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save flow'
      });
    }
  };

  // Flow type icons
  const getTypeIcon = (type: FlowType, size: number = 24) => {
    switch (type) {
      case 'screen_flow':
        return <Monitor size={size} />;
      case 'auto_launched':
        return <Zap size={size} />;
      case 'scheduled':
        return <Clock size={size} />;
      case 'record_triggered':
        return <Database size={size} />;
      case 'platform_event':
        return <Radio size={size} />;
      default:
        return <Zap size={size} />;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Select Flow Type
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FLOW_TYPE_DEFINITIONS.map(def => (
                <FlowTypeCard
                  key={def.type}
                  type={def.type}
                  label={def.label}
                  description={def.description}
                  icon={getTypeIcon(def.type)}
                  color={def.color}
                  selected={selectedType === def.type}
                  onClick={() => setSelectedType(def.type)}
                />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Basic Information
            </h2>
            <StandardInput
              label="Flow Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., New Employee Onboarding"
              required
              error={errors.name}
            />
            <StandardInput
              label="API Name"
              value={apiName}
              onChange={(e) => setApiName(e.target.value)}
              placeholder="e.g., New_Employee_Onboarding"
              required
              error={errors.apiName}
              helpText="Used for programmatic access. Must start with a letter."
            />
            <StandardTextarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this flow does..."
              rows={4}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {selectedType === 'scheduled' && 'Schedule Configuration'}
              {selectedType === 'record_triggered' && 'Record Trigger Configuration'}
              {(selectedType === 'screen_flow' || selectedType === 'auto_launched') && 'Variables'}
              {selectedType === 'platform_event' && 'Event Configuration'}
            </h2>

            {/* Scheduled Flow Config */}
            {selectedType === 'scheduled' && (
              <div className="space-y-4">
                <StandardSelect
                  label="Frequency"
                  value={scheduleFrequency}
                  onChange={(e) => setScheduleFrequency(e.target.value as ScheduleFrequency)}
                  options={[
                    { value: 'once', label: 'Once' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' }
                  ]}
                />
                <div className="grid grid-cols-2 gap-4">
                  <StandardInput
                    label="Start Date"
                    type="date"
                    value={scheduleStartDate}
                    onChange={(e) => setScheduleStartDate(e.target.value)}
                    required
                    error={errors.scheduleStartDate}
                  />
                  <StandardInput
                    label="Start Time"
                    type="time"
                    value={scheduleStartTime}
                    onChange={(e) => setScheduleStartTime(e.target.value)}
                    required
                  />
                </div>
                <StandardSelect
                  label="Timezone"
                  value={scheduleTimezone}
                  onChange={(e) => setScheduleTimezone(e.target.value)}
                  options={TIMEZONES}
                />
              </div>
            )}

            {/* Record-Triggered Flow Config */}
            {selectedType === 'record_triggered' && (
              <div className="space-y-4">
                <StandardSelect
                  label="Object"
                  value={triggerObjectType}
                  onChange={(e) => setTriggerObjectType(e.target.value)}
                  options={[{ value: '', label: 'Select Object' }, ...OBJECT_TYPES]}
                  required
                  error={errors.triggerObjectType}
                />
                <StandardSelect
                  label="Trigger Type"
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value as RecordTriggerType)}
                  options={[
                    { value: 'create', label: 'When a record is created' },
                    { value: 'update', label: 'When a record is updated' },
                    { value: 'create_or_update', label: 'When a record is created or updated' },
                    { value: 'delete', label: 'When a record is deleted' }
                  ]}
                />
                <StandardCheckbox
                  label="Run Asynchronously"
                  checked={runAsync}
                  onChange={(checked) => setRunAsync(checked)}
                  helpText="Run after the transaction completes for better performance"
                />
              </div>
            )}

            {/* Variables for Screen/Auto-Launched Flows */}
            {(selectedType === 'screen_flow' || selectedType === 'auto_launched') && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Define input and output variables for your flow
                  </p>
                  <StandardButton
                    variant="secondary"
                    size="sm"
                    onClick={addVariable}
                    leftIcon={<Plus size={16} />}
                  >
                    Add Variable
                  </StandardButton>
                </div>

                {variables.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">
                      No variables defined yet. Variables are optional.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {variables.map((variable, index) => (
                      <VariableInput
                        key={index}
                        variable={variable}
                        index={index}
                        onChange={updateVariable}
                        onRemove={removeVariable}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Platform Event Config */}
            {selectedType === 'platform_event' && (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                  <AlertCircle size={20} />
                  <span>Platform Event flows require additional setup in the system configuration.</span>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        const typeDef = FLOW_TYPE_DEFINITIONS.find(t => t.type === selectedType);
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Review & Create
            </h2>

            <StandardCard className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg bg-${typeDef?.color || 'blue'}-100 dark:bg-${typeDef?.color || 'blue'}-900/30`}>
                    {getTypeIcon(selectedType)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {name || 'Untitled Flow'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {typeDef?.label} • API Name: {apiName}
                    </p>
                  </div>
                </div>

                {description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {description}
                    </p>
                  </div>
                )}

                {selectedType === 'scheduled' && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Schedule
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {scheduleFrequency.charAt(0).toUpperCase() + scheduleFrequency.slice(1)} starting {scheduleStartDate} at {scheduleStartTime} ({scheduleTimezone})
                    </p>
                  </div>
                )}

                {selectedType === 'record_triggered' && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trigger
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      When a {triggerObjectType} record is {triggerType.replace('_', ' or ')}
                      {runAsync && ' (runs asynchronously)'}
                    </p>
                  </div>
                )}

                {variables.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Variables ({variables.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {variables.filter(v => v.name).map((v, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-700 dark:text-gray-300"
                        >
                          {v.name} ({v.type})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </StandardCard>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Your flow will be created as a <strong>Draft</strong>. After creation, you can add elements using the Flow Builder and then activate it when ready.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {WIZARD_STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {currentStep > step.id ? <Check size={18} /> : step.id}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.id
                      ? 'text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {step.description}
                  </p>
                </div>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded ${
                  currentStep > step.id
                    ? 'bg-green-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <StandardCard className="p-6 mb-6">
        {renderStepContent()}
      </StandardCard>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <StandardButton
          variant="secondary"
          onClick={currentStep === 1 ? onCancel : handleBack}
          leftIcon={currentStep > 1 ? <ChevronLeft size={16} /> : undefined}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </StandardButton>

        {currentStep < 4 ? (
          <StandardButton
            variant="primary"
            onClick={handleNext}
            rightIcon={<ChevronRight size={16} />}
          >
            Next
          </StandardButton>
        ) : (
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            leftIcon={<Check size={16} />}
          >
            {isEditing ? 'Save Changes' : 'Create Flow'}
          </StandardButton>
        )}
      </div>
    </div>
  );
};

export default FlowWizard;
